// src/components/resources/tools/generators/CommentThread/TikTokComments.tsx

'use client'

import type { FC } from 'react'
import { Heart } from 'lucide-react'
import { clsx } from 'clsx'
import {
  countComments,
  displayedLikes,
  formatCompactCount,
  normalizeHandle,
  type ThreadComment,
} from './threadModel'
import { CommentAvatar, likeButtonLabel, type CommentPreviewProps } from './previewShared'

/**
 * TikTok comments sheet, styled to the current mobile UI: centered
 * "Comments (N)" header, muted nickname above the comment text, a
 * `time · Reply` meta row, and the heart + stacked count rail on the right.
 * Creator comments carry the "· Creator" suffix; pinned comments show the
 * rose "Pinned" chip TikTok renders next to the nickname. The heart rail is a
 * real toggle (aria-pressed): clicking fills TikTok red and bumps the count.
 */
export const TikTokComments: FC<CommentPreviewProps> = ({ comments, theme, onToggleLike }) => {
  const isDark = theme === 'dark'
  const text = isDark ? 'text-[#f1f1f2]' : 'text-[#161823]'
  const muted = isDark ? 'text-[#f1f1f2]/50' : 'text-[#161823]/50'
  const border = isDark ? 'border-[#2f2f2f]' : 'border-gray-200'
  const total = countComments(comments)

  const renderRow = (comment: ThreadComment, isReply = false): JSX.Element => (
    <div className={clsx('flex gap-3 py-2.5', isReply ? 'pl-11 pr-4' : 'px-4')}>
      <CommentAvatar username={comment.username} size={isReply ? 'sm' : 'md'} flavor="tiktok" />
      <div className="min-w-0 flex-1">
        <p className={clsx('flex flex-wrap items-center gap-1.5 text-[13px] font-semibold', muted)}>
          {normalizeHandle(comment.username)}
          {comment.byAuthor && <span className="font-normal">· Creator</span>}
          {comment.pinned && !isReply && (
            <span className="rounded-sm bg-[#fe2c55]/10 px-1 py-px text-[10px] font-medium text-[#fe2c55]">
              Pinned
            </span>
          )}
        </p>
        <p className={clsx('mt-0.5 whitespace-pre-wrap text-[14px] leading-snug', text)}>{comment.text}</p>
        <p className={clsx('mt-1 flex items-center gap-4 text-[12px]', muted)}>
          <span>{comment.timestamp}</span>
          <span className="font-semibold">Reply</span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => onToggleLike?.(comment.id)}
        aria-pressed={comment.liked}
        aria-label={likeButtonLabel(comment.liked, comment.username)}
        className={clsx('flex flex-col items-center gap-0.5 rounded p-0.5 pt-1 transition-transform hover:scale-110', muted)}
      >
        <Heart className={clsx('h-4 w-4', comment.liked && 'fill-[#fe2c55] text-[#fe2c55]')} />
        <span className="text-[11px]">{formatCompactCount(displayedLikes(comment))}</span>
      </button>
    </div>
  )

  return (
    <div className={clsx('overflow-hidden rounded-xl border shadow-xl', border, isDark ? 'bg-[#121212]' : 'bg-white')}>
      <div className={clsx('border-b py-2.5 text-center text-[13px] font-semibold', border, text)}>
        Comments ({formatCompactCount(total)})
      </div>
      <div className="py-1">
        {comments.map(comment => (
          <div key={comment.id}>
            {renderRow(comment)}
            {comment.replies.map(reply => (
              <div key={reply.id}>{renderRow(reply, true)}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
